/**
 * InventoryMovementsPage — ניהול הזמנות
 *
 * Tabs:
 *  · הזמנות B2B נכנסות  (B2BOrdersAdminTab)
 *  · החזרות RMA          (RMAAdminTab)
 *
 * Route:  /app/inventory/movements
 * Guard:  module="inventory" level="viewer"
 */

import { useState } from 'react';
import { Loader as Loader2, LayoutList, Boxes, RotateCcw } from 'lucide-react';
import { useCompany }     from '@/context/CompanyContext';
import { B2BOrdersAdminTab } from './B2BOrdersAdminTab';
import { RMAAdminTab }       from './RMAAdminTab';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'b2bOrders' | 'rma';

// ─── Sub-component: tab button ────────────────────────────────────────────────

function TabBtn({
  active, onClick, icon, label, count,
}: {
  active: boolean; onClick: () => void;
  icon: React.ReactNode; label: string; count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
        transition-all duration-150 whitespace-nowrap
        ${active
          ? 'bg-white text-orange-700 shadow-sm border border-orange-100'
          : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'}
      `}
    >
      {icon}
      {label}
      {count !== undefined && (
        <span className={`
          ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full
          ${active ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-500'}
        `}>
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function InventoryMovementsPage() {
  const { activeCompanyId } = useCompany();

  const [activeTab,        setActiveTab]        = useState<Tab>('b2bOrders');
  const [b2bPendingCount,  setB2bPendingCount]  = useState(0);

  const tabs = [
    {
      id:    'b2bOrders' as Tab,
      label: 'הזמנות B2B נכנסות',
      icon:  <Boxes className="w-4 h-4" />,
      count: b2bPendingCount > 0 ? b2bPendingCount : undefined,
    },
    { id: 'rma' as Tab, label: 'החזרות RMA', icon: <RotateCcw className="w-4 h-4" /> },
  ];

  if (!activeCompanyId) {
    return (
      <div className="p-6 flex items-center gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען…</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl">

      {/* Page header */}
      <div className="flex items-start gap-4">
        <div className="p-2.5 bg-orange-100 rounded-xl shrink-0">
          <LayoutList className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ניהול הזמנות</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            הזמנות B2B · החזרות RMA
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1.5 bg-gray-100/80 rounded-2xl p-1.5 w-fit">
        {tabs.map(tab => (
          <TabBtn
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            icon={tab.icon}
            label={tab.label}
            count={tab.count}
          />
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'b2bOrders' && (
        <B2BOrdersAdminTab
          companyId={activeCompanyId}
          onPendingCount={setB2bPendingCount}
        />
      )}
      {activeTab === 'rma' && (
        <RMAAdminTab companyId={activeCompanyId} />
      )}

    </div>
  );
}
